import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Paperclip, FileText, ExternalLink } from 'lucide-react';
import { adminAPI } from '../services/adminApi';
import { renderCorrectedReportHtml, hasCorrections } from '../utils/reportDiff';
import './Reports.css';

function renderReport(text) {
  if (!text) return '';
  const escHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/  /g, ' &nbsp;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  return text.split('\n').map(l => l.trim() ? `<div>${escHtml(l.trim())}</div>` : '<div style="height:14px"></div>').join('');
}

const isImage = (type) => typeof type === 'string' && type.startsWith('image/');

const ReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromFeedback = location.state?.from === 'feedback';
  const backPath = cameFromFeedback ? '/reports/feedbacks' : '/reports';
  const backLabel = cameFromFeedback ? 'Back to Feedback' : 'Back to Reports';

  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('final');
  const [lightbox, setLightbox] = useState(null); 
  const [selectedVersion, setSelectedVersion] = useState(null); 

  useEffect(() => {
    setLoading(true);
    adminAPI.getReportById(id)
      .then(r => {
        setReport(r.data);
        const v = r.data.final_report_versions;
        setSelectedVersion(v?.length ? v[v.length - 1].version : 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })
    : '—';
  const versions = report?.final_report_versions?.length
    ? report.final_report_versions
    : (report?.final_report ? [{ version: 1, final_report: report.final_report, created_at: report.updated_at || report.created_at }] : []);
  const latestVersion = versions.length ? versions[versions.length - 1].version : 1;
  const currentVersionEntry = versions.find(v => v.version === selectedVersion);
  const displayedFinalReport = currentVersionEntry?.final_report ?? report?.final_report ?? '';

  const correctedEntry = (report?.corrected_report_versions || []).find(c => c.version === selectedVersion);
  const correctedForVersion = correctedEntry?.corrected_report
    ?? (selectedVersion === latestVersion ? report?.corrected_report : null)
    ?? null;
  const correctionsExist = hasCorrections(displayedFinalReport, correctedForVersion);

  return (
    <div className="admin-rd-page">
      <button className="admin-rd-back-btn" onClick={() => navigate(backPath)}>
        <ArrowLeft size={16} /> {backLabel}
      </button>

      {loading ? (
        <div className="admin-table-card admin-rd-page-state">Loading…</div>
      ) : !report ? (
        <div className="admin-table-card admin-rd-page-state">Report not found.</div>
      ) : (
        <div className="admin-table-card admin-rd-page-card">
          <div className="admin-rd-page-header">
            <h1 className="admin-page-title">Report Details</h1>
            <span className={`admin-status-badge ${report.status === 'completed' ? 'active' : 'inactive'}`}>{report.status}</span>
          </div>

          <div className="admin-rd-meta">
            <span><strong>User:</strong> {[report.profiles?.first_name, report.profiles?.last_name].filter(Boolean).join(' ') || report.profiles?.email || '—'}</span>
            <span><strong>Type:</strong> {report.report_type ? (report.report_type === 'hospital' ? 'Hospital' : 'Non Hospital') : '—'}</span>
            <span><strong>Modality:</strong> {report.modalities?.name || '—'}</span>
            <span><strong>Body Part:</strong> {report.body_parts?.name || '—'}</span>
          </div>

          <div className="admin-rd-tabs">
            <button className={tab === 'final' ? 'active' : ''} onClick={() => setTab('final')}>Final Report</button>
            <button className={tab === 'corrected' ? 'active' : ''} onClick={() => setTab('corrected')}>
              Corrected Report {correctionsExist && <span className="admin-corrected-dot" title="Has corrections">●</span>}
            </button>
            <button className={tab === 'raw'   ? 'active' : ''} onClick={() => setTab('raw')}>Raw Report</button>
            <button className={tab === 'feedback' ? 'active' : ''} onClick={() => setTab('feedback')}>
              Feedback {report.feedback?.length > 0 && `(${report.feedback.length})`}
            </button>

            {(tab === 'final' || tab === 'corrected') && versions.length > 1 && (
              <select
                className="admin-rd-version-select"
                value={selectedVersion ?? latestVersion}
                onChange={e => setSelectedVersion(Number(e.target.value))}
                title="View a previous conversion of this report"
              >
                {versions.map(v => (
                  <option key={v.version} value={v.version}>
                    Version {v.version}{v.version === latestVersion ? ' (latest)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {(tab === 'final' || tab === 'corrected') && currentVersionEntry && (
            <div className="admin-rd-version-date">Converted: {fmt(currentVersionEntry.created_at)}</div>
          )}

          <div className="admin-rd-content admin-rd-content-page">
            {tab === 'final' && (
              <div dangerouslySetInnerHTML={{ __html: renderReport(displayedFinalReport) || '<em>No final report yet.</em>' }} />
            )}
            {tab === 'corrected' && (
              !correctionsExist ? (
                <em>No corrections made yet — this matches the Final Report.</em>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: renderCorrectedReportHtml(displayedFinalReport, correctedForVersion) }} />
              )
            )}
            {tab === 'raw' && (
              <pre className="admin-rd-raw">{report.raw_report || '—'}</pre>
            )}
            {tab === 'feedback' && (
              report.feedback?.length > 0 ? (
                <div className="admin-rd-feedback-list">
                  {report.feedback.map((f, i) => (
                    <div key={i} className={`admin-rd-feedback-item ${f.deleted ? 'is-deleted' : ''}`}>
                      <div className="admin-rd-feedback-date">
                        {fmt(f.created_at)}
                        {f.deleted && <span className="admin-rd-deleted-badge">Deleted{f.deleted_at ? ` · ${fmt(f.deleted_at)}` : ''}</span>}
                      </div>
                      <p>{f.message}</p>
                      {f.files?.length > 0 && (
                        <div className="admin-rd-feedback-attachments">
                          {f.files.map((file, fi) => (
                            isImage(file.type) ? (
                              <img
                                key={fi}
                                src={file.url}
                                alt={file.name}
                                className="admin-rd-fb-thumb"
                                onClick={() => setLightbox(file.url)}
                              />
                            ) : (
                              <a
                                key={fi}
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="admin-rd-feedback-file-link"
                              >
                                <FileText size={14} /> {file.name} <ExternalLink size={11} />
                              </a>
                            )
                          ))}
                        </div>
                      )}
                      {f.files?.length > 0 && (
                        <div className="admin-rd-feedback-files">
                          <Paperclip size={12} /> {f.files.length} file(s) attached
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : <em>No feedback submitted for this report.</em>
            )}
          </div>
        </div>
      )}

      {lightbox && (
        <div className="admin-lightbox-overlay" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Attachment preview" className="admin-lightbox-img" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default ReportDetail;